import { css } from '@emotion/react'
import { ExternalStyles, HFlow, isEmpty, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import {
  RadioOption,
  resolveName,
  SimNaoEnum,
  SimNaoRadioGroupField,
  SimNaoRadioGroupFieldProps,
} from 'components/form'
import { RequiredLabel } from 'components/form/final-form/RequiredLabel/RequiredLabel'
import { getIn } from 'final-form'
import { ReactNode } from 'react'
import { useForm } from 'react-final-form'
import { MetaPath, MetaRoot } from 'util/metaPath'
import { ConsultaSeguimentoOutroFieldModel } from 'view/atendimentos/detail/soap/objetivo/puericultura/mchat-rf/consulta-seguimento/model-consultaSeguimento'

import { PerguntaSimNaoOutroField } from './PerguntaSimNaoOutroField'

export interface PerguntaSimNaoOutroRadioListFieldProps<T extends string>
  extends Omit<SimNaoRadioGroupFieldProps, 'onChange'> {
  name: MetaPath<Record<T, SimNaoEnum | undefined>> & MetaRoot<Record<T, SimNaoEnum>>
  nameOutro?: MetaPath<ConsultaSeguimentoOutroFieldModel>
  label: ReactNode
  description?: string
  options: RadioOption<T>[]
  required?: boolean
  hideOutrosFieldRadioOptions?: boolean
  labelStyle?: ExternalStyles
  onChange?: (value: Record<T, SimNaoEnum>) => void
}

export function PerguntaSimNaoOutroRadioListField<T extends string>(
  props: Readonly<PerguntaSimNaoOutroRadioListFieldProps<T>>
) {
  const {
    name,
    nameOutro,
    label,
    description,
    options,
    required,
    hideOutrosFieldRadioOptions,
    labelStyle,
    onChange,
    ...rest
  } = props

  const { getState } = useForm()
  const theme = useTheme()
  const styles = createStyles(theme)

  const hasDescription = !isEmpty(description)

  const handleChange = (option: T, value: SimNaoEnum) => {
    if (onChange) {
      const fieldValue = getIn(getState().values, resolveName(name))
      onChange({
        ...fieldValue,
        [option]: value,
      })
    }
  }

  return (
    <VFlow vSpacing={0.5}>
      {hasDescription &&
        (required ? (
          <RequiredLabel fontWeight='bold' label={description} />
        ) : (
          <Text fontWeight='bold'>{description}</Text>
        ))}
      <VFlow vSpacing={0}>
        <Box style={styles.box}>
          <VFlow vSpacing={0} style={styles.list}>
            <div css={styles.header}>
              {required && !hasDescription ? (
                <RequiredLabel fontWeight='bold' label={label} style={labelStyle} />
              ) : (
                <Text fontWeight='bold' style={labelStyle}>
                  {label}
                </Text>
              )}
            </div>
            {options.map((option) => (
              <HFlow key={option.value} alignItems='center' justifyContent='space-between' style={styles.listItem}>
                <Text>{option.label}</Text>
                <SimNaoRadioGroupField
                  name={name[option.value]}
                  hSpacing={0.5}
                  style={{ hFlow: styles.radio }}
                  onChange={(value) => handleChange(option.value, value.currentTarget.value as SimNaoEnum)}
                  {...rest}
                />
              </HFlow>
            ))}
          </VFlow>
        </Box>
        {nameOutro && (
          <PerguntaSimNaoOutroField
            name={nameOutro}
            hideRadioOptions={hideOutrosFieldRadioOptions}
            style={styles.listItem}
            onChange={onChange}
          />
        )}
      </VFlow>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  box: css`
    padding: 0;
    border-bottom: 0;
  `,
  list: css`
    & > :not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.gray.c80};
    }
  `,
  header: css`
    padding: 0.875rem 1rem;
  `,
  listItem: css`
    padding: 0.75rem 1rem;
  `,
  radio: css`
    justify-content: flex-end;
  `,
})
